import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ApiCacheTags, baseApiService } from '@qapline/api';
import { useLazyGetAttachmentFileQuery } from '@qapline/core';
import { performFileDownload } from '@qapline/helpers';
import { IconDownload } from 'assets/icons';
import { useAppDispatch } from 'hooks';
import { Button, ButtonProps } from 'modules/ui';

export interface IDownloadAttachmentFileButtonProps extends ButtonProps {
    attachmentId: number;
    hideText?: boolean;
}

export const DownloadAttachmentFileButton: React.FC<IDownloadAttachmentFileButtonProps> = ({
    attachmentId,
    hideText,
    disabled,
    ...props
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [getAttachmentFile, { isFetching }] = useLazyGetAttachmentFileQuery();

    const handleDownload = async () => {
        try {
            const { data, headers } = await getAttachmentFile({ attachmentId, download: true }, false).unwrap();
            dispatch(
                baseApiService.util.invalidateTags([
                    { type: ApiCacheTags.attachment.item },
                    { type: ApiCacheTags.attachment.list },
                    { type: ApiCacheTags.invoice.list },
                    { type: ApiCacheTags.commission.list },
                ]),
            );
            const fileName = headers.get('Content-Disposition')?.split('filename=')[1] || '';
            performFileDownload(data, decodeURIComponent(fileName));
        } catch (error: any) {
            if (error?.data?.message) {
                return toast.error(error.data.message);
            }
            toast.error(t('attachments.download.error'));
        }
    };
    return (
        <>
            <Button
                Icon={hideText ? <IconDownload /> : undefined}
                type="button"
                variant="secondary"
                onClick={handleDownload}
                disabled={isFetching || disabled}
                isLoading={isFetching}
                {...props}
            >
                {!hideText && <IconDownload />}
                {!hideText && t('common.subheader.download')}
            </Button>
        </>
    );
};
